import { logger, NetworkError, ValidationError } from '@/utils/logger';

export interface ContactFormData {
  name: string;
  email: string;
  company?: string;
  message: string;
}

export interface ContactFormResponse {
  success: boolean;
  ticketId?: string;
  message?: string;
  error?: string;
}

/**
 * Submit contact form to create a Zendesk ticket
 */
export async function submitContactForm(
  formData: ContactFormData
): Promise<ContactFormResponse> {
  const apiUrl = import.meta.env.VITE_CONTACT_SERVICE_URL;

  if (!apiUrl) {
    const errorMsg = 'Contact service URL not configured';
    logger.error(errorMsg);
    throw new ValidationError(errorMsg);
  }

  // Validate required fields
  if (!formData.name || !formData.email || !formData.message) {
    const errorMsg = 'Missing required fields: name, email, and message are required';
    logger.error('Contact form validation failed:', errorMsg);
    throw new ValidationError(errorMsg);
  }

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formData.email)) {
    const errorMsg = 'Invalid email address format';
    logger.error('Email validation failed:', formData.email);
    throw new ValidationError(errorMsg);
  }

  try {
    logger.info('Submitting contact form for:', formData.email);

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMsg = data.error || `HTTP ${response.status}: ${response.statusText}`;
      logger.error('Contact form submission failed:', {
        status: response.status,
        error: errorMsg,
        email: formData.email,
      });
      throw new NetworkError(errorMsg, response.status);
    }

    if (!data.success) {
      const errorMsg = data.error || 'Failed to submit contact form';
      logger.error('Contact form API returned error:', errorMsg);
      throw new Error(errorMsg);
    }

    logger.info('Contact form submitted successfully. Ticket ID:', data.ticketId);

    return {
      success: true,
      ticketId: data.ticketId,
      message: data.message || 'Contact form submitted successfully',
    };
  } catch (error) {
    if (error instanceof NetworkError || error instanceof ValidationError) {
      throw error;
    }

    const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
    logger.error('Unexpected error submitting contact form:', error);
    throw new Error(`Failed to submit contact form: ${errorMsg}`);
  }
}
